// @ts-nocheck
/**
 * ツインキーシステム - パーティ編成画面
 * @module twinKey/ui/screens/PartyScreen
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTwinKeyContext } from '../../context/TwinKeyContext';
import { useParty, useTwinKey } from '../../hooks';
import { filterCardsByAttribute } from '../../core/keySystem';
import { calculateAllStats } from '../../core/party';
import { createSkillInstances } from '../../data/skills';
import { RARITY_CONFIG } from '../../data/constants';
import { EQUIPMENT_SLOTS, EQUIPMENT_SLOT_UNLOCK } from '../../features/equipment/equipmentData';
import { equipmentManager } from '../../features/equipment/EquipmentManager';
import styles from '../styles/twinKeyStyles';

const MAX_PARTY_SIZE = 5;
const ATTRIBUTES = ['すべて', '炎', '水', '光', '闇', '原始', '未来', 'なし'];

/**
 * パーティ編成画面
 */
export function PartyScreen() {
  const { state, actions, allCards, playerData } = useTwinKeyContext();
  const {
    activeParty,
    addPartyMember,
    removePartyMember,
    swapPartyMembers,
    createNewParty,
  } = useParty();
  const { goToHub } = useTwinKey();

  // パーティが存在しない場合：保存済みがあれば読み込み、なければ新規作成
  useEffect(() => {
    if (!activeParty) {
      // 保存済みパーティを確認
      const savedParties = playerData?.twinKey?.parties;
      const activePartyId = playerData?.twinKey?.activePartyId;

      if (savedParties && savedParties.length > 0) {
        // 保存済みパーティを読み込む
        const partyToLoad = activePartyId
          ? savedParties.find(p => p.id === activePartyId) || savedParties[0]
          : savedParties[0];
        if (partyToLoad) {
          console.log('[PartyScreen] 保存されたパーティを読み込み:', partyToLoad);
          actions.setActiveParty(partyToLoad);
          return;
        }
      }
      // 保存済みがなければ新規作成
      createNewParty('メインパーティ');
    }
  }, [activeParty, playerData, actions, createNewParty]);

  // フィルター状態
  const [attributeFilter, setAttributeFilter] = useState('すべて');
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('rarity'); // ソート順

  // 選択中のメンバー（詳細表示用）
  const [selectedMember, setSelectedMember] = useState(null);
  // 装備選択モーダル
  const [equipmentModal, setEquipmentModal] = useState(null); // { member, slot }

  // プレイヤーの所持装備リスト
  const playerEquipment = useMemo(() => {
    return playerData?.twinKey?.equipment || [];
  }, [playerData]);

  // 装備を装着するハンドラ
  const handleEquip = useCallback((member, slot, equipment) => {
    if (!activeParty) return;

    // パーティメンバーに装備を設定
    const updatedMembers = activeParty.members.map(m => {
      if (m.uniqueId !== member.uniqueId) return m;

      const currentEquipment = { ...(m.equipment || {}) };
      const previousEquip = currentEquipment[slot];
      currentEquipment[slot] = equipment;

      return { ...m, equipment: currentEquipment };
    });

    const updatedParty = { ...activeParty, members: updatedMembers, updatedAt: Date.now() };
    actions.setActiveParty(updatedParty);

    // 装備モーダルを閉じる
    setEquipmentModal(null);

    // selectedMemberを更新
    const updatedMember = updatedMembers.find(m => m.uniqueId === member.uniqueId);
    if (updatedMember) setSelectedMember(updatedMember);
  }, [activeParty, actions]);

  // 装備を外すハンドラ
  const handleUnequip = useCallback((member, slot) => {
    if (!activeParty) return;

    const updatedMembers = activeParty.members.map(m => {
      if (m.uniqueId !== member.uniqueId) return m;

      const currentEquipment = { ...(m.equipment || {}) };
      currentEquipment[slot] = null;

      return { ...m, equipment: currentEquipment };
    });

    const updatedParty = { ...activeParty, members: updatedMembers, updatedAt: Date.now() };
    actions.setActiveParty(updatedParty);

    // selectedMemberを更新
    const updatedMember = updatedMembers.find(m => m.uniqueId === member.uniqueId);
    if (updatedMember) setSelectedMember(updatedMember);
  }, [activeParty, actions]);

  // 装備がすでに他モンスターに装備されているかチェック
  const isEquipmentUsed = useCallback((equipmentId) => {
    if (!activeParty?.members) return false;
    return activeParty.members.some(m => {
      const eq = m.equipment || {};
      return Object.values(eq).some(e => e?.id === equipmentId);
    });
  }, [activeParty]);

  // 所持モンスターカードを取得
  const availableMonsters = useMemo(() => {
    if (!allCards || !playerData?.collection) return [];

    const monsters = [];
    const collection = playerData.collection || [];

    // コレクションは配列形式: [{ cardId, rarity, quantity }, ...]
    collection.forEach((item) => {
      if (item.quantity <= 0) return;

      const { cardId, rarity } = item;
      const cardIdRarity = `${cardId}_${rarity}`;

      const baseCard = allCards.find(c => c.id === cardId);
      if (baseCard && baseCard.type === 'monster') {
        // モンスター成長データからレベルと覚醒を取得
        const monsterProgress = playerData.twinKey?.monsterProgress?.[cardId] || {};
        const level = monsterProgress.level || 1;
        const awakening = monsterProgress.awakening || 0;

        // ステータス計算
        const stats = calculateAllStats(baseCard, level, rarity, awakening);

        // スキル情報を生成
        const skills = createSkillInstances(baseCard, level);

        monsters.push({
          ...baseCard,
          rarity,
          uniqueKey: cardIdRarity,
          level,
          awakening,
          stats,
          skills,
        });
      }
    });

    return monsters;
  }, [allCards, playerData]);

  // フィルタリング済みモンスター
  const filteredMonsters = useMemo(() => {
    let monsters = [...availableMonsters];

    // 属性フィルター
    if (attributeFilter !== 'すべて') {
      monsters = filterCardsByAttribute(monsters, attributeFilter);
    }

    // 検索フィルター
    if (searchText) {
      const search = searchText.toLowerCase();
      monsters = monsters.filter(m =>
        m.name.toLowerCase().includes(search) ||
        (m.category && m.category.toLowerCase().includes(search))
      );
    }

    // ソート（statsプロパティ対応のカスタムソート）
    monsters.sort((a, b) => {
      switch (sortBy) {
        case 'attack':
          return (b.stats?.attack || 0) - (a.stats?.attack || 0); // 高い順
        case 'hp':
          return (b.stats?.maxHp || 0) - (a.stats?.maxHp || 0); // 高い順
        case 'level':
          return (b.level || 1) - (a.level || 1); // 高い順
        case 'name':
          return (a.name || '').localeCompare(b.name || ''); // あいうえお順
        case 'rarity':
        default:
          // レアリティ順（高い順）
          const rarityOrder = { 'C': 1, 'UC': 2, 'R': 3, 'SR': 4, 'UR': 5, 'HR': 6, 'SEC': 7, 'ALT': 7, 'SP': 8, 'GR': 9 };
          return (rarityOrder[b.rarity] || 0) - (rarityOrder[a.rarity] || 0);
      }
    });

    return monsters;
  }, [availableMonsters, attributeFilter, searchText, sortBy]);

  // パーティに追加済みかチェック
  const isInParty = (monster) => {
    return activeParty?.members?.some(m => m.uniqueKey === monster.uniqueKey) || false;
  };

  // モンスター追加ハンドラ
  const handleAddMember = (monster) => {
    if ((activeParty?.members?.length || 0) >= MAX_PARTY_SIZE) return;
    if (isInParty(monster)) return;

    addPartyMember({
      ...monster,
      uniqueId: `${monster.uniqueKey}_${Date.now()}`,
    });
  };

  // パーティメンバー並べ替え
  const handleMoveUp = (index) => {
    if (index === 0) return;
    swapPartyMembers(index, index - 1);
  };

  const handleMoveDown = (index) => {
    if (index === (activeParty?.members?.length || 0) - 1) return;
    swapPartyMembers(index, index + 1);
  };

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={goToHub}>
          ← 戻る
        </button>
        <h1 style={styles.title}>パーティ編成</h1>
        <div style={styles.subtitle}>
          {activeParty?.members?.length || 0} / {MAX_PARTY_SIZE}
        </div>
      </div>

      {/* メインコンテンツ */}
      <div style={styles.partyScreenContent}>
        {/* パーティスロット */}
        <div style={styles.partySection}>
          <h2 style={styles.sectionTitle}>現在のパーティ</h2>
          <div style={styles.partySlots}>
            {Array(MAX_PARTY_SIZE).fill(null).map((_, index) => {
              const member = activeParty?.members?.[index];
              return (
                <PartySlot
                  key={index}
                  member={member}
                  index={index}
                  onRemove={() => removePartyMember(index)}
                  onSelect={() => setSelectedMember(member)}
                  onMoveUp={() => handleMoveUp(index)}
                  onMoveDown={() => handleMoveDown(index)}
                  canMoveUp={index > 0}
                  canMoveDown={index < (activeParty?.members?.length || 0) - 1}
                />
              );
            })}
          </div>

          {/* パーティ合計ステータス */}
          <div style={styles.partyTotalStats}>
            <div style={styles.totalStatItem}>
              <span style={styles.totalStatLabel}>総攻撃力</span>
              <span style={styles.totalStatValue}>
                {(activeParty?.members || []).reduce((sum, m) => sum + (m?.stats?.attack || 0), 0)}
              </span>
            </div>
            <div style={styles.totalStatItem}>
              <span style={styles.totalStatLabel}>総HP</span>
              <span style={styles.totalStatValue}>
                {(activeParty?.members || []).reduce((sum, m) => sum + (m?.stats?.maxHp || 0), 0)}
              </span>
            </div>
          </div>
        </div>

        {/* モンスター選択 */}
        <div style={styles.monsterSelectionSection}>
          <h2 style={styles.sectionTitle}>モンスター一覧</h2>

          {/* フィルター */}
          <div style={styles.filterRow}>
            <input
              type="text"
              placeholder="検索..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={styles.searchInputSmall}
            />
            <select
              value={attributeFilter}
              onChange={(e) => setAttributeFilter(e.target.value)}
              style={styles.filterSelectSmall}
            >
              {ATTRIBUTES.map(attr => (
                <option key={attr} value={attr}>{attr}</option>
              ))}
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={styles.filterSelectSmall}
            >
              <option value="rarity">レアリティ順</option>
              <option value="attack">ATK順</option>
              <option value="hp">HP順</option>
              <option value="level">レベル順</option>
              <option value="name">名前順</option>
            </select>
          </div>

          {/* モンスターリスト */}
          <div style={styles.monsterListContainer}>
            <div style={styles.monsterList}>
              {filteredMonsters.length === 0 ? (
                <div style={styles.emptyMessage}>
                  モンスターがありません
                </div>
              ) : (
                filteredMonsters.map(monster => (
                  <MonsterListItem
                    key={monster.uniqueKey}
                    monster={monster}
                    inParty={isInParty(monster)}
                    disabled={(activeParty?.members?.length || 0) >= MAX_PARTY_SIZE}
                    onAdd={() => handleAddMember(monster)}
                    onSelect={() => setSelectedMember(monster)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 詳細モーダル */}
      {selectedMember && (
        <MonsterDetailModal
          monster={selectedMember}
          onClose={() => setSelectedMember(null)}
          onOpenEquipmentModal={(slot) => setEquipmentModal({ member: selectedMember, slot })}
          onUnequip={(slot) => handleUnequip(selectedMember, slot)}
        />
      )}

      {/* 装備選択モーダル */}
      {equipmentModal && (
        <EquipmentSelectionModal
          member={equipmentModal.member}
          slot={equipmentModal.slot}
          playerEquipment={playerEquipment}
          isEquipmentUsed={isEquipmentUsed}
          onSelect={(equipment) => handleEquip(equipmentModal.member, equipmentModal.slot, equipment)}
          onClose={() => setEquipmentModal(null)}
        />
      )}
    </div>
  );
}

/**
 * パーティスロット
 */
function PartySlot({
  member,
  index,
  onRemove,
  onSelect,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}) {
  if (!member) {
    return (
      <div style={styles.emptyPartySlot}>
        <div style={styles.emptySlotNumber}>{index + 1}</div>
        <div style={styles.emptySlotText}>空きスロット</div>
      </div>
    );
  }

  const attrColor = styles.getAttributeColor(member.attribute);
  const rarityColor = styles.getRarityColor(member.rarity);

  return (
    <div
      style={{
        ...styles.partySlot,
        borderColor: attrColor,
      }}
    >
      {/* 順番変更ボタン */}
      <div style={styles.reorderButtons}>
        <button
          style={{
            ...styles.reorderButton,
            opacity: canMoveUp ? 1 : 0.3,
          }}
          onClick={onMoveUp}
          disabled={!canMoveUp}
        >
          ▲
        </button>
        <button
          style={{
            ...styles.reorderButton,
            opacity: canMoveDown ? 1 : 0.3,
          }}
          onClick={onMoveDown}
          disabled={!canMoveDown}
        >
          ▼
        </button>
      </div>

      {/* メンバー情報 */}
      <div style={styles.partyMemberContent} onClick={onSelect}>
        <div
          style={{
            ...styles.memberAttributeIcon,
            background: attrColor,
          }}
        >
          {styles.getAttributeIcon(member.attribute)}
        </div>
        <div style={styles.memberDetails}>
          <div
            style={{
              ...styles.memberNameText,
              color: rarityColor,
            }}
          >
            {member.name}
          </div>
          <div style={styles.memberLevelText}>
            Lv.{member.level}
            {member.awakening > 0 && ` ☆${member.awakening}`}
          </div>
          <div style={styles.memberStatsText}>
            ATK: {member.stats?.attack || 0} / HP: {member.stats?.maxHp || 0}
          </div>
        </div>
      </div>

      {/* 削除ボタン */}
      <button style={styles.removeButton} onClick={onRemove}>
        ×
      </button>
    </div>
  );
}

/**
 * モンスターリストアイテム
 */
function MonsterListItem({ monster, inParty, disabled, onAdd, onSelect }) {
  const attrColor = styles.getAttributeColor(monster.attribute);
  const rarityColor = styles.getRarityColor(monster.rarity);

  return (
    <div
      style={{
        ...styles.monsterListItem,
        borderColor: attrColor,
        opacity: (inParty || disabled) ? 0.5 : 1,
      }}
    >
      <div style={styles.monsterItemMain} onClick={onSelect}>
        <div
          style={{
            ...styles.monsterAttrIcon,
            background: attrColor,
          }}
        >
          {styles.getAttributeIcon(monster.attribute)}
        </div>
        <div style={styles.monsterItemInfo}>
          <div
            style={{
              ...styles.monsterItemName,
              color: rarityColor,
            }}
          >
            {monster.name}
          </div>
          <div style={styles.monsterItemMeta}>
            <span>{monster.rarity}</span>
            <span>Lv.{monster.level}</span>
            {monster.awakening > 0 && <span>☆{monster.awakening}</span>}
          </div>
          <div style={styles.monsterItemStats}>
            ATK: {monster.stats?.attack || 0} / HP: {monster.stats?.maxHp || 0}
          </div>
        </div>
      </div>

      <button
        style={{
          ...styles.addButton,
          ...(inParty || disabled ? styles.disabledButton : {}),
        }}
        onClick={onAdd}
        disabled={inParty || disabled}
      >
        {inParty ? '編成済' : '+'}
      </button>
    </div>
  );
}

/**
 * モンスター詳細モーダル
 */
function MonsterDetailModal({ monster, onClose, onOpenEquipmentModal, onUnequip }) {
  const attrColor = styles.getAttributeColor(monster.attribute);
  const rarityColor = styles.getRarityColor(monster.rarity);

  // 装備スロット情報を取得
  const monsterLevel = monster.level || 1;
  const equipmentSlots = [
    { key: EQUIPMENT_SLOTS.WEAPON, name: '武器', icon: '⚔️', unlockLevel: EQUIPMENT_SLOT_UNLOCK[EQUIPMENT_SLOTS.WEAPON] },
    { key: EQUIPMENT_SLOTS.ARMOR, name: '防具', icon: '🛡️', unlockLevel: EQUIPMENT_SLOT_UNLOCK[EQUIPMENT_SLOTS.ARMOR] },
    { key: EQUIPMENT_SLOTS.SPECIAL, name: '特殊', icon: '✨', unlockLevel: EQUIPMENT_SLOT_UNLOCK[EQUIPMENT_SLOTS.SPECIAL] },
    { key: EQUIPMENT_SLOTS.ACCESSORY, name: 'アクセ', icon: '💍', unlockLevel: EQUIPMENT_SLOT_UNLOCK[EQUIPMENT_SLOTS.ACCESSORY] },
  ];

  // 装備によるステータス補正を計算
  const equipmentModifiers = useMemo(() => {
    return equipmentManager.calculateEquipmentModifiers(monster);
  }, [monster]);

  // 基礎ステータス + 装備補正
  const totalStats = useMemo(() => {
    const baseAttack = monster.stats?.attack || 0;
    const baseHp = monster.stats?.maxHp || 0;
    const baseDefense = monster.stats?.defense || 0;
    const baseSpeed = monster.stats?.speed || 0;

    return {
      attack: Math.floor(baseAttack * (1 + equipmentModifiers.atkPercent) + equipmentModifiers.atkFlat),
      hp: Math.floor(baseHp * (1 + equipmentModifiers.hpPercent) + equipmentModifiers.hpFlat),
      defense: baseDefense + equipmentModifiers.defFlat,
      speed: baseSpeed + equipmentModifiers.spdFlat,
    };
  }, [monster.stats, equipmentModifiers]);

  // ステータスに装備補正があるかチェック
  const hasEquipmentBonus = (stat) => {
    const base = monster.stats?.[stat === 'hp' ? 'maxHp' : stat] || 0;
    return totalStats[stat] !== base;
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={{ ...styles.modalContent, maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <button style={styles.modalCloseButton} onClick={onClose}>
          ×
        </button>

        {/* モンスター名 */}
        <div style={styles.modalHeader}>
          <div
            style={{
              ...styles.modalAttrIcon,
              background: attrColor,
            }}
          >
            {styles.getAttributeIcon(monster.attribute)}
          </div>
          <div>
            <div
              style={{
                ...styles.modalMonsterName,
                color: rarityColor,
              }}
            >
              {monster.name}
            </div>
            <div style={styles.modalMonsterMeta}>
              {monster.rarity} | Lv.{monster.level}
              {monster.awakening > 0 && ` | 覚醒☆${monster.awakening}`}
            </div>
          </div>
        </div>

        {/* ステータス（装備補正込み） */}
        <div style={styles.modalStatsSection}>
          <h3 style={styles.modalSectionTitle}>ステータス</h3>
          <div style={styles.modalStatsGrid}>
            <div style={styles.modalStatItem}>
              <span style={styles.modalStatLabel}>攻撃力</span>
              <span style={{
                ...styles.modalStatValue,
                color: hasEquipmentBonus('attack') ? '#4ade80' : 'inherit',
              }}>
                {totalStats.attack}
              </span>
            </div>
            <div style={styles.modalStatItem}>
              <span style={styles.modalStatLabel}>HP</span>
              <span style={{
                ...styles.modalStatValue,
                color: hasEquipmentBonus('hp') ? '#4ade80' : 'inherit',
              }}>
                {totalStats.hp}
              </span>
            </div>
            <div style={styles.modalStatItem}>
              <span style={styles.modalStatLabel}>防御</span>
              <span style={{
                ...styles.modalStatValue,
                color: hasEquipmentBonus('defense') ? '#4ade80' : 'inherit',
              }}>
                {totalStats.defense}
              </span>
            </div>
            <div style={styles.modalStatItem}>
              <span style={styles.modalStatLabel}>速度</span>
              <span style={{
                ...styles.modalStatValue,
                color: hasEquipmentBonus('speed') ? '#4ade80' : 'inherit',
              }}>
                {totalStats.speed}
              </span>
            </div>
          </div>
        </div>

        {/* 装備スロット */}
        <div style={styles.modalStatsSection}>
          <h3 style={styles.modalSectionTitle}>装備</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {equipmentSlots.map((slot) => {
              const isUnlocked = monsterLevel >= slot.unlockLevel;
              const equippedItem = monster.equipment?.[slot.key];

              return (
                <EquipmentSlotDisplay
                  key={slot.key}
                  slot={slot}
                  isUnlocked={isUnlocked}
                  equippedItem={equippedItem}
                  onTap={() => isUnlocked && onOpenEquipmentModal(slot.key)}
                  onUnequip={() => onUnequip(slot.key)}
                />
              );
            })}
          </div>
        </div>

        {/* スキル */}
        <div style={styles.modalSkillsSection}>
          <h3 style={styles.modalSectionTitle}>スキル</h3>
          <div style={styles.modalSkillsList}>
            {monster.skills?.map((skill, index) => (
              <div key={index} style={styles.modalSkillItem}>
                <div style={styles.skillHeader}>
                  <span style={styles.skillName}>{skill.name || `スキル${index + 1}`}</span>
                  <span style={styles.skillCooldown}>CT: {skill.cooldown}T</span>
                </div>
                <div style={styles.skillDescription}>{skill.description}</div>
              </div>
            )) || (
              <div style={styles.noSkills}>スキル情報なし</div>
            )}
          </div>
        </div>

        {/* カテゴリ */}
        {monster.category && (
          <div style={styles.modalCategorySection}>
            <span style={styles.categoryLabel}>カテゴリ:</span>
            <span style={styles.categoryValue}>{monster.category}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 装備スロット表示コンポーネント
 */
function EquipmentSlotDisplay({ slot, isUnlocked, equippedItem, onTap, onUnequip }) {
  // ティア別の色
  const TIER_COLORS = {
    common: '#888',
    rare: '#3498db',
    epic: '#9b59b6',
    legendary: '#ffd700',
  };

  if (!isUnlocked) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 12px',
        background: 'rgba(50, 50, 70, 0.4)',
        borderRadius: '8px',
        border: '1px solid rgba(100, 100, 120, 0.3)',
        opacity: 0.6,
      }}>
        <span style={{ fontSize: '20px', marginRight: '10px' }}>🔒</span>
        <div>
          <div style={{ fontSize: '12px', color: '#888' }}>{slot.name}</div>
          <div style={{ fontSize: '10px', color: '#666' }}>Lv.{slot.unlockLevel}で解放</div>
        </div>
      </div>
    );
  }

  if (!equippedItem) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 12px',
          background: 'rgba(60, 60, 80, 0.5)',
          borderRadius: '8px',
          border: '1px dashed rgba(139, 92, 246, 0.4)',
          cursor: 'pointer',
        }}
        onClick={onTap}
      >
        <span style={{ fontSize: '20px', marginRight: '10px', opacity: 0.5 }}>{slot.icon}</span>
        <div>
          <div style={{ fontSize: '12px', color: '#aaa' }}>{slot.name}</div>
          <div style={{ fontSize: '10px', color: '#888' }}>タップして装備</div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
        background: 'rgba(139, 92, 246, 0.15)',
        borderRadius: '8px',
        border: `1px solid ${TIER_COLORS[equippedItem.tier] || '#666'}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', flex: 1, cursor: 'pointer' }} onClick={onTap}>
        <span style={{ fontSize: '20px', marginRight: '10px' }}>{slot.icon}</span>
        <div>
          <div style={{ fontSize: '12px', color: '#fff', fontWeight: 'bold' }}>
            {equippedItem.name}
          </div>
          <div style={{ fontSize: '10px', color: TIER_COLORS[equippedItem.tier] || '#888' }}>
            Lv.{equippedItem.level} {'★'.repeat(equippedItem.awakening || 0)}
            {equippedItem.stats?.attack && ` ATK+${equippedItem.stats.attack}`}
            {equippedItem.stats?.hp && ` HP+${equippedItem.stats.hp}`}
          </div>
        </div>
      </div>
      <button
        style={{
          padding: '4px 8px',
          background: 'rgba(239, 68, 68, 0.3)',
          border: '1px solid rgba(239, 68, 68, 0.5)',
          borderRadius: '4px',
          color: '#ef4444',
          fontSize: '10px',
          cursor: 'pointer',
        }}
        onClick={(e) => {
          e.stopPropagation();
          onUnequip();
        }}
      >
        外す
      </button>
    </div>
  );
}

/**
 * 装備選択モーダル
 */
function EquipmentSelectionModal({ member, slot, playerEquipment, isEquipmentUsed, onSelect, onClose }) {
  const SLOT_TYPE_MAP = {
    [EQUIPMENT_SLOTS.WEAPON]: 'weapon',
    [EQUIPMENT_SLOTS.ARMOR]: 'armor',
    [EQUIPMENT_SLOTS.ACCESSORY]: 'accessory',
    [EQUIPMENT_SLOTS.SPECIAL]: 'special',
  };

  const SLOT_NAMES = {
    [EQUIPMENT_SLOTS.WEAPON]: '武器',
    [EQUIPMENT_SLOTS.ARMOR]: '防具',
    [EQUIPMENT_SLOTS.ACCESSORY]: 'アクセサリ',
    [EQUIPMENT_SLOTS.SPECIAL]: '特殊',
  };

  const TIER_COLORS = {
    common: '#888',
    rare: '#3498db',
    epic: '#9b59b6',
    legendary: '#ffd700',
  };

  // このスロットに装備可能なアイテムをフィルタ
  const availableEquipment = useMemo(() => {
    const slotType = SLOT_TYPE_MAP[slot];
    return playerEquipment.filter(eq => eq.type === slotType);
  }, [playerEquipment, slot]);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
    }} onClick={onClose}>
      <div style={{
        background: '#1a1a2e',
        borderRadius: '12px',
        padding: '20px',
        maxWidth: '400px',
        width: '90%',
        maxHeight: '70vh',
        overflow: 'auto',
        border: '2px solid rgba(139, 92, 246, 0.5)',
      }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#ffd700',
          marginBottom: '16px',
          textAlign: 'center',
        }}>
          {SLOT_NAMES[slot]}を選択
        </h2>

        {availableEquipment.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: '#888',
            padding: '40px 0',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📦</div>
            <div style={{ fontSize: '14px' }}>装備できる{SLOT_NAMES[slot]}がありません</div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
              魔導錬金所で作成してください
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {availableEquipment.map((eq) => {
              const isUsed = isEquipmentUsed(eq.id);
              const isCurrentlyEquipped = member.equipment?.[slot]?.id === eq.id;

              return (
                <div
                  key={eq.id}
                  style={{
                    padding: '12px',
                    background: isCurrentlyEquipped
                      ? 'rgba(139, 92, 246, 0.3)'
                      : isUsed
                        ? 'rgba(50, 50, 70, 0.3)'
                        : 'rgba(60, 60, 80, 0.5)',
                    borderRadius: '8px',
                    border: `1px solid ${isCurrentlyEquipped ? '#8b5cf6' : TIER_COLORS[eq.tier] || '#666'}`,
                    cursor: isUsed && !isCurrentlyEquipped ? 'not-allowed' : 'pointer',
                    opacity: isUsed && !isCurrentlyEquipped ? 0.5 : 1,
                  }}
                  onClick={() => {
                    if (!isUsed || isCurrentlyEquipped) {
                      onSelect(eq);
                    }
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '4px',
                  }}>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#fff',
                    }}>
                      {eq.name}
                    </span>
                    <span style={{
                      fontSize: '10px',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: TIER_COLORS[eq.tier] || '#666',
                      color: eq.tier === 'legendary' ? '#000' : '#fff',
                    }}>
                      {eq.tier?.toUpperCase() || 'COMMON'}
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#aaa' }}>
                    Lv.{eq.level} {'★'.repeat(eq.awakening || 0)}
                  </div>
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                    {eq.stats?.attack && `ATK +${eq.stats.attack} `}
                    {eq.stats?.hp && `HP +${eq.stats.hp} `}
                    {eq.stats?.defense && `DEF +${eq.stats.defense} `}
                    {eq.stats?.speed && `SPD +${eq.stats.speed}`}
                  </div>
                  {isCurrentlyEquipped && (
                    <div style={{
                      fontSize: '10px',
                      color: '#8b5cf6',
                      marginTop: '4px',
                    }}>
                      ✓ 装備中
                    </div>
                  )}
                  {isUsed && !isCurrentlyEquipped && (
                    <div style={{
                      fontSize: '10px',
                      color: '#ef4444',
                      marginTop: '4px',
                    }}>
                      他のモンスターが装備中
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <button
          style={{
            width: '100%',
            marginTop: '16px',
            padding: '12px',
            background: 'transparent',
            border: '1px solid rgba(139, 92, 246, 0.5)',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '14px',
            cursor: 'pointer',
          }}
          onClick={onClose}
        >
          閉じる
        </button>
      </div>
    </div>
  );
}

export default PartyScreen;
